import * as THREE from 'three';
import {
  DOMINANT_HAND,
  GUARD_AIM_HEIGHT,
  GUARD_AIM_MIN_FORWARD_COS,
  GUARD_AIM_SIDE,
  GUARD_HAND_HEIGHT,
  GUARD_HAND_ROLL,
  GUARD_SIGNATURES,
  GUARD_TUNING_DEFAULT,
  type DominantHand,
  type Guard,
  type GuardAimHeight,
  type GuardAimSide,
  type GuardHandHeight,
  type GuardHandRoll,
  type GuardSignature,
  type GuardTuning,
} from '../config/constants';

export type { Guard };

/**
 * The binned RELATIONAL feature of a held pose. `aimSide` is `null` when the blade doesn't aim
 * forward at the enemy (point up/sideways/back → a high guard). Exposed for the HUD / debug.
 */
export type GuardFeature = {
  handHeight: GuardHandHeight;
  aimHeight: GuardAimHeight;
  aimSide: GuardAimSide | null;
  /** Hand roll about the grip: terza / seconda, or `null` when the held roll is neither. */
  handRoll: GuardHandRoll | null;
};

/** Where the blade ray lands on the enemy: world point + lateral (enemy's right +) and vertical (rel. eyes). */
export type AimHit = { point: THREE.Vector3; lateral: number; vertical: number };

export type GuardInput = {
  /** Grip (hand) world position. */
  handPos: THREE.Vector3;
  /** Blade axis (unit, base→tip) world. */
  bladeAxis: THREE.Vector3;
  /** True-edge (filo dritto) direction, world. Its roll about the blade gives terza vs seconda. */
  edgeDir: THREE.Vector3;
  /** Enemy eye world point (the directrix anchor + stretta target). */
  enemyEyes: THREE.Vector3;
  /** Enemy belt world point (the larga target). */
  enemyBelt: THREE.Vector3;
  /** User eye (headset) world position — the other end of the directrix. */
  userEyes: THREE.Vector3;
  /** User height (m) — normalizes the hand height. */
  userHeightM: number;
  /** Dominant (sword) hand — mirrors porte/code. */
  dominantHand: DominantHand;
};

const UP = new THREE.Vector3(0, 1, 0);

/**
 * Where the blade ray hits the enemy's frontal plane (through the eyes, normal = the horizontal
 * directrix to the enemy), or `null` when the blade doesn't aim forward at the enemy. Pure.
 */
export function aimOnEnemy(
  handPos: THREE.Vector3,
  bladeAxis: THREE.Vector3,
  enemyEyes: THREE.Vector3,
  userEyes: THREE.Vector3,
): AimHit | null {
  const directrix = enemyEyes.clone().sub(userEyes);
  directrix.y = 0;
  if (directrix.lengthSq() < 1e-9) return null;
  directrix.normalize();

  const denom = bladeAxis.dot(directrix);
  if (denom < GUARD_AIM_MIN_FORWARD_COS) return null; // not aiming forward at the enemy

  const t = enemyEyes.clone().sub(handPos).dot(directrix) / denom;
  if (t <= 0) return null;

  const point = handPos.clone().addScaledVector(bladeAxis, t);
  const rel = point.clone().sub(enemyEyes);
  const enemyRight = new THREE.Vector3().crossVectors(UP, directrix).normalize();
  return { point, lateral: rel.dot(enemyRight), vertical: rel.y };
}

/**
 * Classify a held pose into a single guardia, or `null` (fuori guardia) when the relational
 * feature matches no guard — or more than one. Pure: no side effects, no mutation.
 */
export function classifyGuard(
  input: GuardInput,
  tuning: GuardTuning = GUARD_TUNING_DEFAULT,
): Guard | null {
  return matchGuard(guardFeature(input, tuning));
}

/** Match an already-binned feature to a single guard, or `null`. (Split out for the recognizer.) */
export function matchGuard(feature: GuardFeature | null): Guard | null {
  if (!feature) return null;
  const hits = (Object.keys(GUARD_SIGNATURES) as Guard[]).filter((g) =>
    signatureMatches(GUARD_SIGNATURES[g], feature),
  );
  return hits.length === 1 ? hits[0]! : null;
}

/** Compute the binned relational feature, or `null` when an input vector is non-finite. */
export function guardFeature(
  input: GuardInput,
  tuning: GuardTuning = GUARD_TUNING_DEFAULT,
): GuardFeature | null {
  const { handPos, bladeAxis, edgeDir, enemyEyes, enemyBelt, userEyes, userHeightM, dominantHand } =
    input;
  if (
    !finite3(handPos) ||
    !finite3(bladeAxis) ||
    !finite3(edgeDir) ||
    !finite3(enemyEyes) ||
    !finite3(enemyBelt) ||
    !finite3(userEyes) ||
    !(userHeightM > 0)
  ) {
    return null;
  }

  const handHeight =
    handPos.y / userHeightM >= tuning.handAltaMinFrac
      ? GUARD_HAND_HEIGHT.alta
      : GUARD_HAND_HEIGHT.bassa;
  const handRoll = binHandRoll(bladeAxis, edgeDir, tuning);

  const aim = aimOnEnemy(handPos, bladeAxis, enemyEyes, userEyes);
  if (!aim) return { handHeight, aimHeight: GUARD_AIM_HEIGHT.fuori, aimSide: null, handRoll };

  return {
    handHeight,
    aimHeight: binAimHeight(aim.vertical, enemyBelt.y - enemyEyes.y, tuning),
    aimSide: binAimSide(aim.lateral, dominantHand, tuning),
    handRoll,
  };
}

/**
 * Hand roll → terza / seconda from the true edge. The edge is made perpendicular to the blade
 * (Gram-Schmidt, as the ghost does), then read against world up: pointing down (−edge·up ≥
 * downMin) is terza (thumb up); near-horizontal (|edge·up| ≤ flatMax) is seconda (knuckles up).
 * Anything between is `null` (fuori guardia). Handedness-agnostic — the roll mirrors with the body.
 */
function binHandRoll(
  bladeAxis: THREE.Vector3,
  edgeDir: THREE.Vector3,
  t: GuardTuning,
): GuardHandRoll | null {
  const perp = edgeDir.clone().addScaledVector(bladeAxis, -edgeDir.dot(bladeAxis));
  if (perp.lengthSq() < 1e-9) return null; // edge parallel to the blade → undecidable
  const up = perp.normalize().y;
  if (-up >= t.handRollDownMin) return GUARD_HAND_ROLL.terza;
  if (Math.abs(up) <= t.handRollFlatMax) return GUARD_HAND_ROLL.seconda;
  return null;
}

function binAimHeight(vertical: number, beltVy: number, t: GuardTuning): GuardAimHeight {
  if (Math.abs(vertical) <= t.aimVertBandM) return GUARD_AIM_HEIGHT.occhi;
  if (Math.abs(vertical - beltVy) <= t.aimVertBandM) return GUARD_AIM_HEIGHT.cintola;
  return GUARD_AIM_HEIGHT.fuori;
}

/**
 * Lateral aim → side. Beyond the figure's half-width = distesa; otherwise porte (enemy's right)
 * / code (enemy's left) by the sign, mirrored for the dominant hand, with a centre dead-band that
 * reads as `centro` (ambiguous → fuori guardia, since the strette must lean to one side).
 */
function binAimSide(lateral: number, dominantHand: DominantHand, t: GuardTuning): GuardAimSide {
  if (Math.abs(lateral) > t.aimSideOnFigM) return GUARD_AIM_SIDE.distesa;
  const m = lateral * (dominantHand === DOMINANT_HAND.right ? 1 : -1);
  if (Math.abs(m) < t.aimCentroM) return GUARD_AIM_SIDE.centro;
  return m > 0 ? GUARD_AIM_SIDE.porte : GUARD_AIM_SIDE.code;
}

/** True when every pinned axis of the signature equals the feature (a `null` axis never matches). */
function signatureMatches(sig: GuardSignature, f: GuardFeature): boolean {
  if (sig.handHeight !== undefined && sig.handHeight !== f.handHeight) return false;
  if (sig.aimHeight !== undefined && sig.aimHeight !== f.aimHeight) return false;
  if (sig.aimSide !== undefined && sig.aimSide !== f.aimSide) return false;
  if (sig.handRoll !== undefined && sig.handRoll !== f.handRoll) return false;
  return true;
}

function finite3(v: THREE.Vector3): boolean {
  return isFinite(v.x) && isFinite(v.y) && isFinite(v.z);
}
